
import React, { useState } from "react";
import { Message } from "@/types/message";
import FollowUpSuggestions from "./FollowUpSuggestions";
import ErrorMessage from "./message/ErrorMessage";
import MessageContent from "./message/MessageContent";
import SystemPromptDebug from "./message/SystemPromptDebug";

interface MessageBubbleProps {
  message: Message;
  showDebugInfo?: boolean;
  onSendMessage?: (content: string) => void;
  suggestions?: string[];
  showSuggestions?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ 
  message, 
  showDebugInfo = false, 
  onSendMessage,
  suggestions = [],
  showSuggestions = true
}) => {
  // State for system prompt visibility
  const [isSystemPromptVisible, setIsSystemPromptVisible] = useState(false);
  
  // Determine if this is an error message or quota exceeded message
  const isError = message.error === true;
  
  const handleSuggestionClick = (question: string) => {
    if (onSendMessage) {
      onSendMessage(question);
    }
  };

  const toggleSystemPrompt = () => {
    setIsSystemPromptVisible(prevState => !prevState);
  };
  
  return (
    <div className={`message-container ${message.isBot ? "" : "justify-end"}`}>
      <div
        className={`message-bubble ${message.isBot ? "bot-message" : "user-message"} ${isError ? "error-message" : ""}`}
      >
        {/* Show system prompt debug only for bot messages when debug mode is enabled AND isSystemPromptVisible is true */}
        {message.isBot && 
         showDebugInfo && 
         isSystemPromptVisible && (
          <SystemPromptDebug 
            systemPrompt={message.systemPrompt}
            fullPromptContext={message.fullPromptContext}
          />
        )}
        
        {/* Error state with special styling for API errors */}
        {isError && <ErrorMessage errorType={message.errorType} errorDetails={message.errorDetails} />}
        
        {/* Regular message content with markdown rendering */}
        <MessageContent 
          message={message} 
          showDebugInfo={showDebugInfo}
          onToggleSystemPrompt={toggleSystemPrompt}
          isSystemPromptVisible={isSystemPromptVisible}
        />
        
        {/* Display suggested follow-up questions only for bot messages and not error messages */}
        {message.isBot && !isError && onSendMessage && (
          <FollowUpSuggestions 
            onSuggestionClick={handleSuggestionClick} 
            suggestions={suggestions}
            show={showSuggestions}
          />
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
